import isWebos from '@accedo/xdk-device-lg-webos/esm/detection'
import isTizen from '@accedo/xdk-device-samsung-tizen/esm/detection'
import isVizio from '@accedo/xdk-device-vizio-smartcast-contrib/esm/detection'
import { Lightning, Router } from '@lightningjs/sdk'

import Button from '../../../components/button/button'
import PointerBackButton from '../../../components/NavigationPointers/PointerBackButton'
import { getColor, grey300, grey50, grey920, transparent } from '../../../constants/colors'
import { UniNeueBold, UniNeueHeavy, UniNeueRegular } from '../../../constants/fonts'
import { LEFT_SAFE_ZONE_MARGIN } from '../../../constants/general'
import { Images } from '../../../constants/paths'
import { LG_SIGN_UP, LOGIN, SIGN_UP, VIZIO_SIGN_UP } from '../../../constants/routes'
import { AppConfig } from '../../../services/appConfig'
import { deleteUserData, getUserData, isUserSignedIn } from '../../../utils/authentication'
import { formatDate } from '../../../utils/datetime'
import { getText } from '../../../utils/dictionary'
import { getAsset } from '../../../utils/image'
import {
  captureClick,
  captureScreenData,
  captureSessionData,
  NON_BFF_SEGMENT_SCREENS,
  SEGMENT_TRACK_EVENTS,
} from '../../../utils/segmentAnalytics'
import { logoutEvent } from '../../../widgets/logOut'
import PageHandler from '../../handlers/pageHandler'

interface HistoryState {
  /** Stores the focused ref when navigating away. */
  lastFocus?: string
}

export enum AccountDetailsStates {
  Unauthenticated = 'Unauthenticated',
  Authenticated = 'Authenticated',
}

export default class AccountDetails extends PageHandler {
  private _isUserSignedIn = isUserSignedIn()
  private _userData = getUserData()
  private _currentButton
  private _sectionText = ''

  static _template() {
    return {
      w: 1920,
      h: 1080,
      rect: true,
      color: transparent,
      flex: {
        direction: 'row',
        paddingLeft: LEFT_SAFE_ZONE_MARGIN,
      },
      LeftContainer: {
        w: 872,
        h: 1080,
        rect: true,
        color: transparent,
        Title: {
          y: 48,
          text: {
            text: getText('accountDetails'),
            fontSize: 64,
            fontFace: UniNeueHeavy,
            textColor: getColor(grey50),
            lineHeight: 72,
          },
        },
        Icon: {
          x: 133,
          y: 248,
          texture: Lightning.Tools.getSvgTexture(getAsset(Images.SETTINGS_USER), 472, 454),
        },
        PointerBackButton: {
          type: PointerBackButton,
          x: 1648,
          y: 48,
        },
      },
      RightContainer: {
        h: 1080,
        rect: true,
        color: transparent,
        InfoCard: {
          y: 200,
          w: 808,
          h: 800,
          rect: true,
          color: getColor(grey920),
          shader: {
            type: Lightning.shaders.RoundedRectangle,
            radius: 40,
          },
          Unauthenticated: {
            w: (w) => w,
            h: (h) => h,
            flex: {
              direction: 'column',
              alignItems: 'center',
              paddingTop: 40,
            },
            Title: {
              text: {
                text: getText('loginHeader'),
                fontSize: 28,
                fontFace: UniNeueBold,
                textColor: getColor(grey50),
                lineHeight: 49,
              },
            },
            Login: {
              w: 668,
              type: Button,
              label: getText('loginButton'),
            },
            SignUp: {
              w: 668,
              type: Button,
              label: getText('signUpButton'),
              flexItem: { marginTop: 32 },
            },
          },
          Authenticated: {
            w: (w) => w,
            h: (h) => h,
            flex: {
              direction: 'column',
              paddingTop: 40,
            },
            UserData: {
              x: 40,
              flex: {
                direction: 'column',
              },
              UsernameHeader: {
                text: {
                  text: getText('usernameHeader'),
                  fontSize: 28,
                  fontFace: UniNeueRegular,
                  textColor: getColor(grey300),
                },
              },
              UsernameValue: {
                flexItem: { marginBottom: 16 },
                text: {
                  text: '',
                  fontSize: 32,
                  fontFace: UniNeueBold,
                  textColor: getColor(grey50),
                  lineHeight: 36,
                },
              },
              EmailHeader: {
                text: {
                  text: getText('emailHeader'),
                  fontSize: 28,
                  fontFace: UniNeueRegular,
                  textColor: getColor(grey300),
                },
              },
              EmailValue: {
                flexItem: { marginBottom: 16 },
                text: {
                  text: '',
                  fontSize: 32,
                  fontFace: UniNeueBold,
                  textColor: getColor(grey50),
                  lineHeight: 36,
                },
              },
              MemberSinceHeader: {
                text: {
                  text: getText('memberSinceHeader'),
                  fontSize: 28,
                  fontFace: UniNeueRegular,
                  textColor: getColor(grey300),
                },
              },
              MemberSinceValue: {
                flexItem: { marginBottom: 16 },
                text: {
                  text: '',
                  fontSize: 32,
                  fontFace: UniNeueBold,
                  textColor: getColor(grey50),
                  lineHeight: 36,
                },
              },
              CountryHeader: {
                text: {
                  text: getText('countryHeader'),
                  fontSize: 28,
                  fontFace: UniNeueRegular,
                  textColor: getColor(grey300),
                },
              },
              CountryValue: {
                flexItem: { marginBottom: 16 },
                text: {
                  text: '',
                  fontSize: 32,
                  fontFace: UniNeueBold,
                  textColor: getColor(grey50),
                  lineHeight: 36,
                },
              },
            },
            Buttons: {
              y: 80,
              flex: {
                direction: 'column',
                alignItems: 'center',
              },
              ChangePassword: {
                w: 668,
                type: Button,
                label: getText('changePasswordButton'),
                flexItem: { marginBottom: 32 },
              },
              Logout: {
                w: 668,
                type: Button,
                label: getText('logoutButton'),
              },
            },
          },
        },
      },
    }
  }

  get unauthenticated() {
    return this.tag('Unauthenticated')
  }

  get authenticated() {
    return this.tag('Authenticated')
  }

  get usernameValue() {
    return this.tag('UsernameValue')
  }

  get emailValue() {
    return this.tag('EmailValue')
  }

  get memberSinceValue() {
    return this.tag('MemberSinceValue')
  }

  get countryValue() {
    return this.tag('CountryValue')
  }

  get loginButton() {
    return this.tag('Login')
  }

  get signUpButton() {
    return this.tag('SignUp')
  }

  get changePasswordButton() {
    return this.tag('ChangePassword')
  }

  get logoutButton() {
    return this.tag('Logout')
  }

  get pageText(): string {
    return getText('accountDetails')
  }

  set sectionText(v: string) {
    this._sectionText = v
  }

  get sectionText(): string {
    return this._sectionText
  }

  get pointerBackButton() {
    return this.tag('PointerBackButton') as PointerBackButton
  }

  static _states() {
    return [
      class Unauthenticated extends this {
        $enter() {
          this._currentButton ??= this.loginButton
        }

        _getFocused() {
          return this._currentButton
        }

        _handleDown() {
          if (this._currentButton === this.loginButton) {
            this._currentButton = this.signUpButton
          }
        }

        _handleUp() {
          if (this._currentButton === this.signUpButton) {
            this._currentButton = this.loginButton
          }
        }

        _handleEnter() {
          if (this._currentButton === this.loginButton) {
            void captureClick(
              SEGMENT_TRACK_EVENTS.ButtonClicked,
              'Log In',
              'Settings',
              'Account Details',
            )
            Router.navigate(LOGIN)
          } else if (this._currentButton === this.signUpButton) {
            void captureClick(
              SEGMENT_TRACK_EVENTS.ButtonClicked,
              'Sign Up',
              'Settings',
              'Account Details',
            )
            if (isWebos) {
              Router.navigate(LG_SIGN_UP)
            } else if (isVizio && !window?.VIZIO?.Account?.isSVODEnabled()) {
              Router.navigate(VIZIO_SIGN_UP)
            } else {
              Router.navigate(SIGN_UP)
            }
          }
        }

        _handleClick() {
          this._handleEnter()
        }
      },
      class Authenticated extends this {
        $enter() {
          this._currentButton = this.changePasswordButton
        }

        _getFocused() {
          return this._currentButton
        }

        _handleDown() {
          if (this._currentButton === this.changePasswordButton) {
            this._currentButton = this.logoutButton
          }
        }

        _handleUp() {
          if (this._currentButton === this.logoutButton) {
            this._currentButton = this.changePasswordButton
          }
        }

        _handleEnter() {
          const eventName = (this._currentButton as Button).label.text.text
          void captureClick(
            SEGMENT_TRACK_EVENTS.ButtonClicked,
            eventName,
            'Settings',
            'Account Details',
          )

          if (this._currentButton === this.logoutButton) {
            Router.focusWidget('Logout')
          } else {
            Router.focusWidget('ChangePassword')
          }
        }

        _handleClick() {
          this._handleEnter()
        }
      },
      class Ready extends this {
        $enter() {
          // State Ready is set by the ExitWindow, so we need to restore the state accordingly
          if (isUserSignedIn()) {
            this._setState(AccountDetailsStates.Authenticated)
          } else {
            this._setState(AccountDetailsStates.Unauthenticated)
          }
        }
      },
    ]
  }

  _init() {
    if (this._isUserSignedIn) {
      this.unauthenticated.visible = false
      this.renderUserData()
      this._setState(AccountDetailsStates.Authenticated)
    } else {
      this.authenticated.visible = false

      this.sectionText = getText('loginHeader') + ' . . '
      this._setState(AccountDetailsStates.Unauthenticated)
    }

    const { isCursorVisible } = AppConfig.shared.appSettings
    this.pointerBackButton.visible = isCursorVisible
  }

  _active() {
    this.application.on(logoutEvent, () => {
      this.logoutUser()
    })

    this.widgets.logout.visible = false
    this.widgets.changepassword.visible = false

    void captureScreenData(NON_BFF_SEGMENT_SCREENS.Settings, NON_BFF_SEGMENT_SCREENS.AccountDetails)
  }

  _inactive() {
    this.application.off(logoutEvent)
  }

  _handleLeft() {
    Router.focusWidget('Menu')
  }

  historyState(params: HistoryState) {
    if (params?.lastFocus) {
      this._currentButton = this.tag(params.lastFocus)
    } else {
      return {
        lastFocus: this._getFocused()?.ref,
      }
    }
  }

  renderUserData() {
    this.usernameValue.patch({ text: { text: this._userData?.user?.username ?? '-' } })
    this.sectionText = getText('usernameHeader') + ' . ' + this._userData?.user?.username + ' . . '

    this.emailValue.patch({ text: { text: this._userData?.user?.email ?? '-' } })
    this.sectionText += getText('emailHeader') + ' . ' + this._userData?.user?.email + ' . . '

    this.memberSinceValue.patch({
      text: { text: formatDate(this._userData?.user?.created_at) ?? '-' },
    })
    this.sectionText +=
      getText('memberSinceHeader') + ' . ' + formatDate(this._userData?.user?.created_at) + ' . . '

    this.countryValue.patch({ text: { text: this._userData?.user?.country ?? '-' } })
    this.sectionText += getText('countryHeader') + ' . ' + this._userData?.user?.country + ' . . '
  }

  async logoutUser() {
    // Clean up user data
    await captureSessionData()
    deleteUserData()
    AppConfig.shared.appSettings.homeData = null

    if (isTizen) {
      await AppConfig.shared.samsungCWService.clearContinueWatchingList()
    }

    // Update UI
    this.widgets.logout.visible = false
    this._setState(AccountDetailsStates.Unauthenticated)
    this.unauthenticated.visible = true
    this.authenticated.visible = false
    this._currentButton = this.loginButton
    this.sectionText = getText('loginHeader') + ' . . '
    Router.focusPage()
  }

  $updateFocusByHover(button) {
    this._currentButton = button
    this._refocus()
  }

  _onCursorEnter() {
    const { isCursorVisible } = AppConfig.shared.appSettings
    this.pointerBackButton.visible = isCursorVisible
  }

  _onCursorLeave() {
    this._currentButton?._focus()
    this.pointerBackButton.visible = false
  }
}
